import React, { useEffect, useState } from "react";
import {
    Table,
    Modal,
    Space,
    Tag,
    Popconfirm,
    message,
    Input,
    InputNumber,
    Select,
    Checkbox,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import adminService from "@/services/admin.service";
import eventService from "@/services/event.service";
import InspectionBtn from "@/components/InspectionBtn";
import toast from "react-hot-toast";


const SubscriptionManagement = () => {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingSubscription, setEditingSubscription] = useState(null);
    const [eventList, setAllEvents] = useState([])
    const [reportList, setReportList] = useState([])
    const { control, handleSubmit, reset } = useForm({
        defaultValues: {
            name: "",
            max_users: 1,
            max_ships: 0,
            max_events: 0,
            events: [],
            reports: [],

        },
    });

    // Fetch subscriptions
    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const res = await adminService.allsubscription();
            setSubscriptions(res.data);
        } catch (err) {
            toast.error("Failed to fetch subscriptions");
        }
        setLoading(false);
    };

    const getallEvents = async () => {
        const { data } = await eventService.getAllEvents();
        const eventsdetails = data?.map((x) => {
            return {
                label: x?.event_type,
                value: x?.event_type,
            };
        });
        setAllEvents(eventsdetails);

        const reportdata = await adminService.allreports();
        const reportlists = reportdata?.data?.map((x) => {
            return {
                label: x?.report_name,
                value: x?.report_id,
            };
        });
        setReportList(reportlists);

    }

    useEffect(() => {
        fetchSubscriptions();
        getallEvents()
    }, []);

    // Open modal for create / edit
    const openModal = (subscription = null) => {
        if (subscription) {
            setEditingSubscription(subscription);
            reset({
                ...subscription,
                events: subscription.events || [],
            });
        } else {
            setEditingSubscription(null);
            reset({
                name: "",
                max_users: 1,
                max_ships: 0,
                max_events: 0,
                events: [],
                reports: []
            });
        }
        setModalVisible(true);
    };  
    // Submit form
    const onSubmit = async (data) => {
        try {
            const payload = {
                ...data,
                events: data.events || [],
                reports: data?.reports?.map(x => {
                    const geteventinfo = reportList?.find(y => y?.value == x);
                    return {
                        label: geteventinfo?.label,
                        value: geteventinfo?.value
                    }
                })

            };
             if (editingSubscription) {
                await adminService.updateSubscription(editingSubscription.id, payload);
                toast.success("Subscription updated");
            } else {
                await adminService.createsubscription(payload);
                toast.success("Subscription created");
            }
            fetchSubscriptions();
            setModalVisible(false);
        } catch (err) {
            toast.error("Failed to save subscription");
        }
    };

    // Delete
    const deleteSubscription = async (id) => {
        try {
            await adminService.deleteSubscription(id);
            toast.success("Subscription deleted");
            fetchSubscriptions();
        } catch (err) {
            toast.error("Failed to delete subscription");
        }
    };

    // Columns
    const columns = [
        { title: "Name", dataIndex: "name" },
        { title: "Max Users", dataIndex: "max_users" },
        { title: "Max Ships", dataIndex: "max_ships" },
        { title: "Max Events", dataIndex: "max_events" },
        { title: "Max No of Inspec Report", dataIndex: "no_of_inspection_reports" },
        { title: "Max No of Report", dataIndex: "no_of_reports" },
        {
            title: "Reports",
            dataIndex: "reports",
            render: (events) =>
                events?.map((e, i) => (
                    <Tag key={i} color="green" className="mb-1">
                        {e?.label}
                    </Tag>
                )),
        },



        {
            title: "Actions",
            render: (_, record) => (
                <Space>
                    <InspectionBtn
                        icon={<EditOutlined />}
                        onClick={() => openModal(record)}
                        className="bg-blue-500 text-white"
                    >
                        Edit
                    </InspectionBtn>
                    <Popconfirm
                        title="Are you sure to delete this subscription?"
                        onConfirm={() => deleteSubscription(record.id)}
                    >
                        <InspectionBtn
                            icon={<DeleteOutlined />}
                            danger
                            className="bg-red-500 text-white"
                        >
                            Delete
                        </InspectionBtn>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold">Subscription Management</h1>
                <InspectionBtn
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openModal()}
                    className="bg-green-600"
                >
                    New Subscription
                </InspectionBtn>
            </div>

            <Table
                className="spotflash-table"
                rowKey="id"
                dataSource={subscriptions}
                columns={columns}
                loading={loading}
                bordered
            />

            {/* Modal with React Hook Form */}
            <Modal
                open={modalVisible}
                title={editingSubscription ? "Edit Subscription" : "New Subscription"}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit(onSubmit)}
                okText="Save"
                className="rounded-xl"
            >
                <form className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Name</label>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: "Name is required" }}
                            render={({ field }) => (
                                <Input {...field} placeholder="Enter subscription name" />
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Max Users</label>
                            <Controller
                                name="max_users"
                                control={control}
                                render={({ field }) => (
                                    <InputNumber {...field} min={1} className="w-full" />
                                )}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Max Ships</label>
                            <Controller
                                name="max_ships"
                                control={control}
                                render={({ field }) => (
                                    <InputNumber {...field} min={0} className="w-full" />
                                )}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Max Events</label>
                            <Controller
                                name="max_events"
                                control={control}
                                render={({ field }) => (
                                    <InputNumber {...field} min={0} className="w-full" />
                                )}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">No of Inspection Reports Limits</label>
                            <Controller
                                name="no_of_inspection_reports"
                                control={control}
                                render={({ field }) => (
                                    <InputNumber {...field} min={0} className="w-full" />
                                )}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">No of Reports Limits</label>
                            <Controller
                                name="no_of_reports"
                                control={control}
                                render={({ field }) => (
                                    <InputNumber {...field} min={0} className="w-full" />
                                )}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Save to Cloud</label>
                            <Controller
                                control={control}
                                name={`cloud_save`}
                                render={({ field }) => (
                                    <Checkbox {...field} checked={field?.value}>
                                        Save to Cloud
                                    </Checkbox>
                                )}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Inspection Event</label>
                        <Controller
                            name="events"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    mode="tags"
                                    options={eventList}
                                    style={{ width: "100%" }}
                                    placeholder="Add or select events"
                                />
                            )}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Reports</label>
                        <Controller
                            name="reports"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    mode="tags"
                                    options={reportList}
                                    style={{ width: "100%" }}
                                    placeholder="Add or select events"
                                />
                            )}
                        />
                    </div>

                </form>
            </Modal>
        </div>
    );
};

export default SubscriptionManagement;
